import { useMemo, useState } from "react";
import { ChatCircleDots, DotsThree, Heart, Smiley, Trash, Warning, ArrowBendUpLeft } from "@phosphor-icons/react";
import ProtectedAttachment from "@/components/messages/ProtectedAttachment";

const QUICK_REACTIONS = ["❤️", "🔥", "👏", "✈️", "😍", "🧳"];

export function MessageBubble({
    message,
    mine,
    isGroup,
    sharePreview,
    onReply,
    onReact,
    onDeleteForMe,
    onUnsend,
    onMediaClick,
    onPassportCardClick,
}) {
    const [showMenu, setShowMenu] = useState(false);
    const [showReactions, setShowReactions] = useState(false);
    const reactionEntries = useMemo(() => Object.entries(message.reaction_counts || {}), [message.reaction_counts]);
    const hasMedia = Boolean(message.attachments?.some((att) => att.kind === "video" || att.kind === "image") || (message.share_ref && sharePreview));
    const isMediaOnly = !message.text && hasMedia;
    const isReelOrVideoShare = Boolean(
        sharePreview?.entity_type === "reel" ||
        sharePreview?.image_url?.match(/\.(mp4|webm|mov)(\?.*)?$/i)
    );

    const handleReplyClick = (preview) => {
        if (!preview?.id) return;
        const targetEl = document.getElementById(`chat-message-row-${preview.id}`);
        if (targetEl) {
            targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
            targetEl.classList.add("ring-2", "ring-[#D4AF37]", "rounded-3xl", "transition-all", "duration-500");
            setTimeout(() => {
                targetEl.classList.remove("ring-2", "ring-[#D4AF37]", "rounded-3xl");
            }, 1800);
        } else {
            onReply?.(preview);
        }
    };

    return (
        <div id={`chat-message-row-${message.id}`} className={`flex ${mine ? "justify-end" : "justify-start"} items-end gap-2 group relative`} data-testid={`chat-message-row-${message.id}`}>
            {isGroup && !mine && (
                <img
                    src={message.sender_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${message.sender_handle || message.sender_id || "traveler"}`}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover shrink-0 mb-1 border border-white/10"
                    data-testid={`chat-message-sender-avatar-${message.id}`}
                />
            )}

            {/* Action buttons in front of the message bubble */}
            <div className={`flex items-center gap-1 opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150 mb-1 shrink-0 ${mine ? "order-first" : "order-last"}`}>
                <button
                    type="button"
                    onClick={() => onReply?.(message)}
                    className="w-7 h-7 rounded-full grid place-items-center bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition-colors"
                    title="Reply"
                    data-testid={`chat-message-reply-button-${message.id}`}
                >
                    <ArrowBendUpLeft size={13} weight="bold" />
                </button>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setShowReactions((v) => !v)}
                        className="w-7 h-7 rounded-full grid place-items-center bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition-colors"
                        title="Add reaction"
                        data-testid={`chat-message-reaction-button-${message.id}`}
                    >
                        <Smiley size={13} />
                    </button>
                    {showReactions && (
                        <div className={`absolute z-30 bottom-full mb-2 ${mine ? "right-0" : "left-0"} rounded-full px-2 py-1 flex items-center gap-1 shadow-2xl backdrop-blur-md`} style={{ background: "rgba(5,10,17,0.96)", border: "1px solid rgba(212,175,55,0.25)" }} data-testid={`chat-message-reaction-picker-${message.id}`}>
                            {QUICK_REACTIONS.map((emoji) => (
                                <button key={emoji} type="button" onClick={() => { onReact?.(message, emoji); setShowReactions(false); }} className="w-8 h-8 rounded-full grid place-items-center text-base hover:bg-white/10 hover:scale-110 transition-transform" data-testid={`chat-message-reaction-option-${message.id}-${emoji}`}>
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setShowMenu((v) => !v)}
                        className="w-7 h-7 rounded-full grid place-items-center bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition-colors"
                        title="More actions"
                        data-testid={`chat-message-menu-button-${message.id}`}
                    >
                        <DotsThree size={13} weight="bold" />
                    </button>
                    {showMenu && (
                        <div className={`absolute z-30 bottom-full mb-2 ${mine ? "right-0" : "left-0"} rounded-2xl overflow-hidden min-w-[12rem] shadow-2xl backdrop-blur-xl text-white`} style={{ background: "rgba(11,19,32,0.98)", border: "1px solid rgba(212,175,55,0.25)" }} data-testid={`chat-message-menu-${message.id}`}>
                            {mine && message.status === "active" && (
                                <button type="button" onClick={() => { onUnsend?.(message); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-xs text-white/90 hover:bg-white/10 flex items-center gap-2 transition-colors" data-testid={`chat-message-delete-button-${message.id}`}>
                                    <Trash size={14} className="text-white/60" /> Unsend for everyone
                                </button>
                            )}
                            <button type="button" onClick={() => { onDeleteForMe?.(message); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-xs text-white/90 hover:bg-white/10 flex items-center gap-2 transition-colors" data-testid={`chat-message-delete-for-me-button-${message.id}`}>
                                <ChatCircleDots size={14} className="text-white/60" /> Remove from my view
                            </button>
                            {message.my_reaction && (
                                <button type="button" onClick={() => { onReact?.(message, null); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-xs text-white/90 hover:bg-white/10 flex items-center gap-2 transition-colors" data-testid={`chat-message-clear-reaction-button-${message.id}`}>
                                    <Heart size={14} className="text-white/60" /> Clear my reaction
                                </button>
                            )}
                            <button type="button" onClick={() => setShowMenu(false)} className="w-full text-left px-4 py-2.5 text-xs text-red-300 hover:bg-red-500/10 flex items-center gap-2 border-t border-white/5 transition-colors" data-testid={`chat-message-report-button-${message.id}`}>
                                <Warning size={14} className="text-red-400" /> Report message
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className={`max-w-[min(100%,40rem)] ${mine ? "items-end" : "items-start"} flex flex-col gap-1.5`}>
                {isGroup && !mine && (
                    <div className="text-[11px] font-medium tracking-wide text-[#D4AF37] px-1 line-clamp-1" data-testid={`chat-message-sender-name-${message.id}`}>
                        {message.sender_name || message.sender_handle || "Traveler"}
                        {message.sender_handle && <span className="text-white/40 ml-1.5 font-normal">@{message.sender_handle}</span>}
                    </div>
                )}
                <div
                    className={
                        isMediaOnly
                            ? "relative flex flex-col"
                            : `rounded-2xl ${mine ? "rounded-br-[4px]" : "rounded-bl-[4px]"} px-4 py-3 relative`
                    }
                    style={
                        isMediaOnly
                            ? { background: "transparent", border: "none", padding: 0 }
                            : {
                                background: mine
                                    ? "linear-gradient(135deg, rgba(16, 25, 40, 0.95), rgba(11, 19, 32, 0.95))"
                                    : "rgba(11, 19, 32, 0.85)",
                                border: mine
                                    ? "1px solid rgba(212, 175, 55, 0.38)"
                                    : "1px solid rgba(255, 255, 255, 0.08)",
                                color: "white",
                                boxShadow: mine
                                    ? "0 4px 20px rgba(0, 0, 0, 0.25), 0 0 1px rgba(212, 175, 55, 0.2)"
                                    : "0 4px 16px rgba(0, 0, 0, 0.2)",
                            }
                    }
                    data-testid={`chat-message-bubble-${message.id}`}
                >
                    {message.reply_preview && (
                        <button
                            type="button"
                            onClick={() => handleReplyClick(message.reply_preview)}
                            className="w-full text-left rounded-xl px-3 py-2 mb-2 transition-opacity hover:opacity-85"
                            style={{
                                background: "rgba(255, 255, 255, 0.04)",
                                border: mine
                                    ? "1px solid rgba(212, 175, 55, 0.25)"
                                    : "1px solid rgba(255, 255, 255, 0.08)",
                                color: "white",
                            }}
                            data-testid={`chat-message-reply-preview-${message.id}`}
                        >
                            <div className={`text-[10px] uppercase tracking-[0.24em] ${mine ? "text-[#D4AF37]" : "text-white/60"}`}>
                                Replying to {message.reply_preview.sender_name || "traveler"}
                            </div>
                            <div className="text-xs opacity-85 mt-1 line-clamp-2">{message.reply_preview.text || message.reply_preview.type || "Message"}</div>
                        </button>
                    )}

                    {message.text && <div className="text-sm whitespace-pre-wrap leading-relaxed text-white/95" data-testid={`chat-message-text-${message.id}`}>{message.text}</div>}

                    {!!message.attachments?.length && (
                        <div className={`${message.text ? "mt-3" : ""} space-y-2`}>
                            {message.attachments.map((attachment) => (
                                <ProtectedAttachment
                                    key={attachment.id}
                                    attachment={attachment}
                                    mine={mine}
                                    onMediaClick={onMediaClick}
                                    testId={`chat-attachment-${message.id}-${attachment.id}`}
                                />
                            ))}
                        </div>
                    )}

                    {message.share_ref && sharePreview && (
                        <div
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onPassportCardClick) {
                                    onPassportCardClick(sharePreview);
                                } else {
                                    onMediaClick?.({
                                        type: isReelOrVideoShare ? "reel" : (sharePreview.image_url?.match(/\.(mp4|webm|mov)(\?.*)?$/i) ? "video" : "image"),
                                        url: sharePreview.image_url,
                                        title: sharePreview.title,
                                        subtitle: sharePreview.subtitle,
                                        path: sharePreview.path,
                                    });
                                }
                            }}
                            className={`${message.text ? "mt-3" : ""} rounded-[22px] overflow-hidden block max-w-[280px] cursor-pointer group/card transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]`}
                            style={{
                                background: "rgba(11,19,32,0.92)",
                                border: mine ? "1px solid rgba(212,175,55,0.3)" : "1px solid rgba(255,255,255,0.1)",
                                color: "white",
                            }}
                            data-testid={`chat-share-card-${message.id}`}
                            title="Click to preview"
                        >
                            {sharePreview.image_url ? (
                                <div className="aspect-[16/9] max-h-36 overflow-hidden bg-black/40 relative">
                                    {isReelOrVideoShare ? (
                                        <video src={sharePreview.image_url} className="w-full h-full object-cover" muted playsInline loop autoPlay />
                                    ) : (
                                        <img src={sharePreview.image_url} alt={sharePreview.title} className="w-full h-full object-cover group-hover/card:scale-[1.02] transition-transform duration-200" />
                                    )}
                                </div>
                            ) : null}
                            <div className="p-3">
                                <div className="text-[9px] uppercase tracking-[0.28em] text-[#D4AF37]/90 font-medium">Passport {sharePreview.entity_type}</div>
                                <div className="font-semibold text-xs mt-1 line-clamp-2 text-white">{sharePreview.title}</div>
                                {sharePreview.subtitle && <div className="text-[11px] text-white/60 mt-0.5 line-clamp-1">{sharePreview.subtitle}</div>}
                            </div>
                        </div>
                    )}
                </div>

                {reactionEntries.length > 0 && (
                    <div className={`flex flex-wrap gap-1.5 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-reactions-${message.id}`}>
                        {reactionEntries.map(([emoji, count]) => (
                            <button key={emoji} type="button" onClick={() => onReact?.(message, emoji)} className="px-2 py-0.5 rounded-full text-xs border" style={{ background: message.my_reaction === emoji ? "rgba(212,175,55,0.16)" : "rgba(255,255,255,0.04)", borderColor: message.my_reaction === emoji ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.08)", color: "white" }} data-testid={`chat-message-reaction-chip-${message.id}-${emoji}`}>
                                <span className="mr-1">{emoji}</span>{count}
                            </button>
                        ))}
                    </div>
                )}

                <div className={`text-[10px] ${mine ? "text-right" : "text-left"} text-white/45 px-1`} data-testid={`chat-message-meta-${message.id}`}>
                    {message.status === "unsent" ? "Message unsent" : message.read && mine ? "Read" : mine ? "Sent" : "Delivered"} · {new Date(message.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </div>
            </div>
        </div>
    );
}
